import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for animated count-up numbers
 * Triggers animation when element enters viewport
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.target - Target number to count to
 * @param {number} options.duration - Animation duration in milliseconds
 * @param {string} options.suffix - Suffix to append to the number
 * @param {string} options.prefix - Prefix to prepend to the number
 * @param {function} options.formatter - Custom number formatter function
 * @param {Object} options.observerOptions - Intersection observer options
 * @returns {Object} { ref, count, isVisible }
 */
export const useCountUp = (options = {}) => {
  const {
    target = 0,
    duration = 2000,
    suffix = '',
    prefix = '',
    formatter = null,
    observerOptions = {
      threshold: 0.3,
      rootMargin: '0px 0px -100px 0px'
    }
  } = options;

  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          startCountAnimation();
          observer.unobserve(element);
        }
      },
      observerOptions
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [hasAnimated, observerOptions]);

  const easeOutCubic = (t) => {
    return 1 - Math.pow(1 - t, 3);
  };

  const startCountAnimation = () => {
    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easing function
      const easedProgress = easeOutCubic(progress);
      const currentCount = Math.round(startValue + (target - startValue) * easedProgress);
      
      setCount(currentCount);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const formattedCount = () => {
    if (formatter && typeof formatter === 'function') {
      return formatter(count);
    }
    return `${prefix}${count.toLocaleString()}${suffix}`;
  };

  return {
    ref,
    count: formattedCount(),
    rawCount: count,
    isVisible,
    hasAnimated
  };
};

/**
 * Hook for multiple counters with staggered animations
 * @param {Array} counters - Array of counter configurations
 * @param {number} staggerDelay - Delay between each counter start
 * @returns {Object} { containerRef, counters, isVisible }
 */
export const useStaggeredCountUp = (counters = [], staggerDelay = 200) => {
  const [isVisible, setIsVisible] = useState(false);
  const [startTimes, setStartTimes] = useState({});
  const containerRef = useRef(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Set staggered start times for each counter
          const times = {};
          counters.forEach((_, index) => {
            times[index] = Date.now() + (index * staggerDelay);
          });
          setStartTimes(times);
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [counters.length, staggerDelay]);

  return {
    containerRef,
    isVisible,
    startTimes
  };
};

/**
 * Simple percentage counter for progress bars
 * @param {number} target - Target percentage (0-100)
 * @param {number} duration - Animation duration in ms
 * @returns {Object} { ref, percentage, isAnimating }
 */
export const usePercentageCounter = (target = 100, duration = 1500) => {
  const [percentage, setPercentage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAnimating(true);
          setHasAnimated(true);
          startAnimation();
          observer.unobserve(element);
        }
      },
      {
        threshold: 0.5
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [hasAnimated]);

  const startAnimation = () => {
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentPercentage = Math.round(target * easedProgress);
      
      setPercentage(currentPercentage);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  return {
    ref,
    percentage,
    isAnimating,
    hasAnimated
  };
};